import React, { useCallback, useEffect, useState } from "react";
import "../styles/RegressionDiagnosis.css";
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Spinner,
  Text,
} from "@fluentui/react-components";
import {
  ArrowClockwiseRegular,
  CheckmarkCircleFilled,
  DismissCircleFilled,
  ErrorCircleFilled,
  LightbulbFilled,
  SparkleFilled,
  StethoscopeRegular,
  CodeRegular,
  DataTrendingRegular,
  ChevronDownRegular,
  ChevronRightRegular,
  ArrowTrendingRegular,
  MailRegular,
  AddRegular,
  OpenRegular,
} from "@fluentui/react-icons";
import { format, parseISO } from "date-fns";
import type { RegressionDiagnosis, MetricMagnitude } from "../interfaces/RegressionDiagnosis";
import {
  createRegressionEmailDraft,
  downloadRegressionEmailDraft,
  type RegressionEmailContext,
  type RegressionEmailInlineImage,
} from "../features/regression-email/create-regression-email";
import {
  createRegressionDiagnosisWorkItem,
  getRegressionDiagnosis,
} from "./regression-diagnosis-api";

// Formats an increase factor: 11 -> "11×", 1.8 -> "1.8×".
function fmtFactor(f: number): string {
  return `${f >= 10 ? Math.round(f) : f.toFixed(1)}\u00d7`;
}

// Safe inline renderer: turns `code` spans and **bold** into styled elements, no HTML injection.
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  return text.split(/(`[^`]+`|\*\*[^*]+\*\*)/g).map((part, i) => {
    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      return (
        <code key={`${keyPrefix}-c-${i}`} className="rd-code">
          {part.slice(1, -1)}
        </code>
      );
    }
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      return (
        <strong key={`${keyPrefix}-b-${i}`} className="rd-strong">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={`${keyPrefix}-t-${i}`}>{part}</React.Fragment>;
  });
}

// Renders AI prose: paragraphs, "- " bullet lists, inline code and **bold** (safe — React elements only).
function RichText({ text, className }: { text: string; className?: string }) {
  const lines = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const blocks: React.ReactNode[] = [];
  let bullets: string[] = [];

  const flushBullets = () => {
    if (bullets.length === 0) return;
    const items = bullets;
    blocks.push(
      <ul key={`ul-${blocks.length}`} className="rd-list">
        {items.map((b, i) => (
          <li key={i} className="rd-list-item">
            {renderInline(b, `li${blocks.length}-${i}`)}
          </li>
        ))}
      </ul>
    );
    bullets = [];
  };

  for (const line of lines) {
    const m = line.match(/^[-•*]\s+(.*)$/);
    if (m) {
      bullets.push(m[1]);
    } else {
      flushBullets();
      blocks.push(
        <p key={`p-${blocks.length}`} className="rd-para">
          {renderInline(line, `p${blocks.length}`)}
        </p>
      );
    }
  }
  flushBullets();

  return <div className={className}>{blocks}</div>;
}

// Compact, tasteful impact row: "▲ CPU 11×  ·  Waits 440×".
function ImpactRow({ metrics }: { metrics: MetricMagnitude[] }) {
  const regressed = metrics.filter((m) => m.factor && m.factor > 1.2);
  if (regressed.length === 0) return null;
  return (
    <div className="rd-impact">
      <Caption1 className="rd-impact-label">Impact</Caption1>
      <div className="rd-impact-pills">
        {regressed.map((m) => (
          <span key={m.name} className="rd-impact-pill" title={`${m.name} rose ${fmtFactor(m.factor!)} vs baseline`}>
            <ArrowTrendingRegular className="rd-impact-arrow" />
            <span className="rd-impact-name">{m.name.replace(/\s*\(.*\)/, "")}</span>
            <span className="rd-impact-factor">{fmtFactor(m.factor!)}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

interface RegressionDiagnosisPanelProps {
  serviceName: string;
  commitId: string;
  initialDiagnosis: RegressionDiagnosis | null;
  azdoOrg?: string;
  emailContext: RegressionEmailContext;
  createEmailImages: () => Promise<RegressionEmailInlineImage[]>;
}

function buildWorkItemUrl(azdoOrg: string | undefined, workItemId: number | null): string | null {
  const org = azdoOrg?.trim();
  if (!org || workItemId === null || workItemId <= 0) return null;

  return `https://${encodeURIComponent(org)}.visualstudio.com/_workitems/edit/${workItemId}`;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return "";
  try {
    return format(parseISO(iso), "MMM d, yyyy HH:mm");
  } catch {
    return iso;
  }
}

function confidenceTone(confidence: number): "high" | "medium" | "low" {
  if (confidence >= 0.85) return "high";
  if (confidence >= 0.6) return "medium";
  return "low";
}

function ConfidenceMeter({ confidence }: { confidence: number }) {
  const pct = Math.round(confidence * 100);
  const tone = confidenceTone(confidence);
  return (
    <div className={`rd-confidence-meter rd-tone-${tone}`} title={`Model confidence: ${pct}%`}>
      <div className="rd-confidence-track">
        <div className="rd-confidence-fill" style={{ width: `${pct}%` }} />
      </div>
      <Caption1 className="rd-confidence-label">{pct}% confidence</Caption1>
    </div>
  );
}

function EvidenceChips({ diagnosis }: { diagnosis: RegressionDiagnosis }) {
  return (
    <div className="rd-evidence">
      <Badge
        appearance={diagnosis.tracesAvailable ? "filled" : "tint"}
        color={diagnosis.tracesAvailable ? "brand" : "warning"}
        icon={<DataTrendingRegular />}
      >
        {diagnosis.tracesAvailable ? "Traces used" : "No traces"}
      </Badge>
      <Badge
        appearance={diagnosis.diffAvailable ? "filled" : "tint"}
        color={diagnosis.diffAvailable ? "brand" : "warning"}
        icon={<CodeRegular />}
      >
        {diagnosis.diffAvailable ? "Code diff used" : "No code diff"}
      </Badge>
    </div>
  );
}

export function RegressionDiagnosisPanel({
  serviceName,
  commitId,
  initialDiagnosis,
  azdoOrg,
  emailContext,
  createEmailImages,
}: RegressionDiagnosisPanelProps) {
  const [diagnosis, setDiagnosis] = useState<RegressionDiagnosis | null>(initialDiagnosis);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(false);
  const [creatingWorkItem, setCreatingWorkItem] = useState(false);
  const [workItemError, setWorkItemError] = useState(false);
  const [expanded, setExpanded] = useState(false); // default collapsed
  const [emailStatus, setEmailStatus] = useState<{ tone: "success" | "error"; message: string } | null>(null);
  const [creatingEmail, setCreatingEmail] = useState(false);

  useEffect(() => {
    setDiagnosis(initialDiagnosis);
    setEmailStatus(null);
  }, [commitId, initialDiagnosis]);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    setRefreshError(false);
    try {
      setDiagnosis(await getRegressionDiagnosis(serviceName, commitId));
    } catch {
      setRefreshError(true);
    } finally {
      setRefreshing(false);
    }
  }, [serviceName, commitId]);

  const downloadEmailDraft = useCallback(async () => {
    setEmailStatus(null);
    if (!diagnosis) return;

    setCreatingEmail(true);
    try {
      const images = await createEmailImages();
      const draft = createRegressionEmailDraft(emailContext, diagnosis, new Date(), images);
      downloadRegressionEmailDraft(draft);
      setEmailStatus({
        tone: "success",
        message: `Downloaded ${draft.fileName}. Open it in Outlook and review before sending.`,
      });
    } catch (error) {
      setEmailStatus({
        tone: "error",
        message: error instanceof Error ? error.message : "Could not create the email draft.",
      });
    } finally {
      setCreatingEmail(false);
    }
  }, [createEmailImages, diagnosis, emailContext]);

  const createWorkItem = useCallback(async () => {
    setCreatingWorkItem(true);
    setWorkItemError(false);
    try {
      setDiagnosis(await createRegressionDiagnosisWorkItem(serviceName, commitId));
    } catch {
      setWorkItemError(true);
    } finally {
      setCreatingWorkItem(false);
    }
  }, [serviceName, commitId]);

  if (!diagnosis) return null;

  const confirmed = diagnosis.verdict === "confirmed";
  const rejected = diagnosis.verdict === "rejected";
  const isReady = diagnosis.status === "Ready";
  const confidencePctNum =
    diagnosis.confidence !== null ? Math.round(diagnosis.confidence * 100) : null;
  const workItemUrl = buildWorkItemUrl(azdoOrg, diagnosis.workItemId);

  const header = (
    <div
      className="rd-panel-header rd-panel-header-toggle"
      onClick={() => setExpanded((v) => !v)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") setExpanded((v) => !v);
      }}
    >
      <div className="rd-panel-title">
        {expanded ? <ChevronDownRegular className="rd-chevron" /> : <ChevronRightRegular className="rd-chevron" />}
        <SparkleFilled className="rd-header-icon" />
        <div>
          <Text weight="semibold" size={400} className="rd-panel-heading">
            AI Regression Report
          </Text>
          {diagnosis.model && (
            <Caption1 className="rd-panel-sub">
              {diagnosis.model}
              {diagnosis.generatedAt ? ` · ${formatTimestamp(diagnosis.generatedAt)}` : ""}
            </Caption1>
          )}
        </div>
      </div>
      <div className="rd-header-right" onClick={(e) => e.stopPropagation()}>
        {/* Compact verdict shown when collapsed so the card is scannable */}
        {!expanded && isReady && diagnosis.verdict && (
          <Badge
            appearance="tint"
            color={confirmed ? "danger" : "success"}
            icon={confirmed ? <CheckmarkCircleFilled /> : <DismissCircleFilled />}
          >
            {confirmed ? "Regression" : "Dismissed"}
            {confidencePctNum !== null ? ` · ${confidencePctNum}%` : ""}
          </Badge>
        )}
        <Button
          appearance="subtle"
          size="small"
          icon={<ArrowClockwiseRegular />}
          onClick={refresh}
          disabled={refreshing}
        >
          {refreshing ? "Refreshing" : "Refresh"}
        </Button>
      </div>
    </div>
  );

  // Non-ready states
  if (diagnosis.status === "Pending" || diagnosis.status === "Analyzing") {
    return (
      <section className="rd-panel rd-panel-neutral">
        {header}
        <div className="rd-status-row">
          <Spinner size="tiny" />
          <Body1>
            {diagnosis.status === "Pending"
              ? "Queued — waiting for evidence before the AI analyzes this commit."
              : "AI analysis in progress…"}
          </Body1>
        </div>
        {refreshError && <Caption1 className="rd-error">Could not refresh. Try again.</Caption1>}
      </section>
    );
  }

  if (diagnosis.status === "Failed") {
    return (
      <section className="rd-panel rd-panel-neutral">
        {header}
        <div className="rd-status-row">
          <ErrorCircleFilled className="rd-error-icon" />
          <Body1>AI analysis could not be generated for this commit.</Body1>
        </div>
      </section>
    );
  }

  const confidencePct = diagnosis.confidence ?? null;

  return (
    <section className={`rd-panel ${confirmed ? "rd-panel-danger" : rejected ? "rd-panel-success" : "rd-panel-neutral"}`}>
      {header}

      {confirmed && isReady && (
        <div className="rd-actions">
          <Button
            appearance="primary"
            icon={<MailRegular />}
            onClick={downloadEmailDraft}
            disabled={creatingEmail}
          >
            {creatingEmail ? "Creating draft" : "Email"}
          </Button>
          {workItemUrl ? (
            <Button
              as="a"
              appearance="secondary"
              icon={<OpenRegular />}
              href={workItemUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open work item #{diagnosis.workItemId}
            </Button>
          ) : (
            <Button
              appearance="secondary"
              icon={<AddRegular />}
              onClick={createWorkItem}
              disabled={creatingWorkItem}
            >
              {creatingWorkItem ? "Creating work item" : "Create work item"}
            </Button>
          )}
          {workItemError && (
            <Caption1 className="rd-error">Could not create the work item. Try again.</Caption1>
          )}
        </div>
      )}
      {emailStatus && (
        <Caption1
          role={emailStatus.tone === "error" ? "alert" : "status"}
          className={`rd-email-status rd-email-status-${emailStatus.tone}`}
        >
          {emailStatus.message}
        </Caption1>
      )}

      {expanded && (
      <>
      {/* Verdict banner */}
      <div className={`rd-verdict-banner ${confirmed ? "rd-verdict-confirmed" : "rd-verdict-rejected"}`}>
        <div className="rd-verdict-left">
          {confirmed ? (
            <CheckmarkCircleFilled className="rd-verdict-icon" />
          ) : (
            <DismissCircleFilled className="rd-verdict-icon" />
          )}
          <Text weight="semibold" size={300}>
            {confirmed ? "Regression confirmed" : "Dismissed — not a regression"}
          </Text>
        </div>
        {confidencePct !== null && isReady && <ConfidenceMeter confidence={confidencePct} />}
      </div>

      <EvidenceChips diagnosis={diagnosis} />

      {diagnosis.metrics && <ImpactRow metrics={diagnosis.metrics} />}

      {diagnosis.summary && (
        <RichText text={diagnosis.summary} className="rd-summary" />
      )}

      {/* Diagnosis */}
      {diagnosis.diagnosis && (
        <div className="rd-block rd-block-diagnosis">
          <div className="rd-block-title">
            <StethoscopeRegular /> <Text weight="semibold">Diagnosis</Text>
          </div>
          <RichText text={diagnosis.diagnosis} className="rd-block-body" />
        </div>
      )}

      {/* Proposed solution */}
      {diagnosis.proposedSolution && (
        <div className="rd-block rd-block-solution">
          <div className="rd-block-title">
            <LightbulbFilled className="rd-solution-icon" /> <Text weight="semibold">Proposed solution</Text>
          </div>
          <RichText text={diagnosis.proposedSolution} className="rd-block-body" />
        </div>
      )}

      {/* Implicated symbols: intentionally not shown here — redundant with the diagnosis text.
          The field is kept in the payload (RegressionDiagnosis.implicatedSymbols) for the future PR agent. */}

      {/* Links intentionally not shown — redundant with the page's ADO Commit Page button and the
          flamegraph rendered below. URLs are kept in the payload for the future PR agent. */}

      <Caption1 className="rd-disclaimer">
        AI-generated analysis · verify before acting. {refreshError ? "Could not refresh." : ""}
      </Caption1>
      </>
      )}
    </section>
  );
}

export default RegressionDiagnosisPanel;
